import { useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Copy, KeyRound } from "lucide-react";
import { setTempPassword } from "@/lib/api";

const LOGIN_URL = `${window.location.origin}/login`;

const copy = async (text) => {
  try {
    await navigator.clipboard.writeText(text);
    toast.success("Copied");
  } catch (_e) {
    toast.error("Copy failed — select the text and copy it manually.");
  }
};

/** Confirm → set a 96h temporary password → show it with ready-to-send text messages. */
export function TempPasswordDialog({ user, onOpenChange }) {
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const close = () => { setResult(null); onOpenChange(false); };
  const run = async () => {
    setBusy(true);
    const r = await setTempPassword(user.user_id);
    setBusy(false);
    if (r.error) toast.error(r.error.message);
    else setResult(r.data);
  };

  const expires = result && new Date(result.expires_at).toLocaleString("en-AU", {
    weekday: "short", day: "numeric", month: "short", hour: "numeric", minute: "2-digit",
  });
  const msg1 = result && `Your timeslot.au login: go to ${LOGIN_URL} and sign in with ${result.email}. I'll text your temporary password separately.`;
  const msg2 = result && `timeslot.au temporary password: ${result.password} (valid until ${expires}). You'll be asked to choose your own password.`;

  return (
    <Dialog open={!!user} onOpenChange={(v) => !v && close()}>
      <DialogContent data-testid="temp-password-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><KeyRound className="h-4 w-4" /> Set temporary password</DialogTitle>
          <DialogDescription>
            {result
              ? "Send these by text (not email). The user must choose their own password when they sign in."
              : <>This replaces <strong>{user?.email}</strong>&apos;s current password with a temporary one valid for 96 hours. No email is sent.</>}
          </DialogDescription>
        </DialogHeader>
        {result && (
          <div className="space-y-3">
            <div className="rounded-md border bg-slate-50 p-3">
              <div className="text-xs uppercase tracking-wide text-slate-500">Temporary password</div>
              <div className="flex items-center justify-between gap-2">
                <code className="text-lg font-semibold" data-testid="temp-password-value">{result.password}</code>
                <Button size="sm" variant="outline" onClick={() => copy(result.password)} data-testid="temp-password-copy"><Copy className="h-3.5 w-3.5" /></Button>
              </div>
              <div className="text-xs text-slate-500 mt-1" data-testid="temp-password-expiry">Expires {expires}</div>
            </div>
            {[["Text 1", msg1, "temp-password-msg1"], ["Text 2", msg2, "temp-password-msg2"]].map(([label, text, id]) => (
              <div key={id} className="rounded-md border p-3 text-sm">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-xs uppercase tracking-wide text-slate-500">{label}</span>
                  <Button size="sm" variant="ghost" onClick={() => copy(text)} data-testid={`${id}-copy`}><Copy className="h-3.5 w-3.5 mr-1" /> Copy</Button>
                </div>
                <p data-testid={id}>{text}</p>
              </div>
            ))}
          </div>
        )}
        <DialogFooter>
          {result ? (
            <Button onClick={close} data-testid="temp-password-done">Done</Button>
          ) : (
            <>
              <Button variant="outline" onClick={close} data-testid="temp-password-cancel">Cancel</Button>
              <Button onClick={run} disabled={busy} data-testid="temp-password-confirm">{busy ? "Setting…" : "Set temporary password"}</Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
